import { cilHome, cilPencil, cilTrash } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  CButton,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CSpinner,
  CBadge,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CRow,
  CCol,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SweetAlert from "../../../helper/SweetAlert";
import adminAxiosInstance from "../../../config/index";

// Fallback error image
const errorImage = "https://via.placeholder.com/80x60?text=No+Image";

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null); // New state for selected property
  const adminToken = localStorage.getItem("token");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const [limit] = useState(7);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true); // ← new
  const [lastId, setLastId] = useState(null);
  const navigate = useNavigate();

  const fetchProperties = async (isLoadMore = false) => {
    if (loading) return;
    setLoading(true);

    try {
      let url = `/property/getAll?limit=${limit}&showCount=true`;
      if (isLoadMore && lastId) {
        url += `&lastId=${lastId}`;
      }

      const response = await adminAxiosInstance.get(url, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const data = response?.data?.data || {};
      const newProperties = data.properties || [];

      setProperties((prev) => {
        if (!isLoadMore) return newProperties;

        // Safety: remove possible duplicates
        const seen = new Set(prev.map((p) => p._id));
        const actuallyNew = newProperties.filter((p) => !seen.has(p._id));

        console.log("Adding", actuallyNew.length, "new items");

        return [...prev, ...actuallyNew];
      });

      setHasMore(newProperties.length === limit);

      if (newProperties.length > 0) {
        const newestId = newProperties[newProperties.length - 1]._id;
        console.log("Setting next lastId →", newestId);
        setLastId(newestId);
      } else {
        setLastId(null);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      SweetAlert({
        icon: "error",
        text: error?.response?.data?.message || "Error fetching properties",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSync = async () => {
    setIsLoading(true);
    try {
      const response = await adminAxiosInstance.get("/property/sync", {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (response.status === 200) {
        SweetAlert({
          icon: "success",
          text: response?.data?.message || "Admin registered successfully!",
          title: "Success!",
        });
      }
    } catch (error) {
      console.log("error", error);
      setIsLoading(false);
      SweetAlert({
        icon: "error",
        text: error?.response?.data?.message || "Error fetching properties",
        title: "Error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPropertyStats = async () => {
    setStatsLoading(true);
    try {
      const response = await adminAxiosInstance.get("/property/count", {
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      if (response?.data?.status === 200) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load property stats:", error);
      // Optional: show SweetAlert error here
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchPropertyStats();
    fetchProperties(false); // initial load
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this property?",
    );
    if (!confirmDelete) return;

    setDeleteLoading(id);
    try {
      await adminAxiosInstance.delete(`/property/delete/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setProperties(properties.filter((property) => property._id !== id));
      setDeleteLoading(null);
      SweetAlert({
        icon: "success",
        text: "Property deleted successfully!",
        title: "Success",
      });
    } catch (error) {
      console.log("error", error);
      setDeleteLoading(null);
      SweetAlert({
        icon: "error",
        text: error?.response?.data?.message || "Error deleting property",
        title: "Error",
      });
    }
  };

  const formatPrice = (price, currency = "AED") => {
    return `${currency} ${price?.toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      "For Sale": "success",
      "For Rent": "info",
      Sold: "secondary",
      Rented: "warning",
    };
    return <CBadge color={statusColors[status] || "primary"}>{status}</CBadge>;
  };

  const getPrimaryImage = (images) => {
    const primary = images?.find((img) => img.isPrimary);
    return primary?.url || images?.[0]?.url || errorImage;
  };

  const handleView = (propertyId) => {
    const property = properties.find((p) => p._id === propertyId);
    setSelectedProperty(property);
  };

  const closeViewModal = () => {
    setSelectedProperty(null);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <CIcon icon={cilHome} size="xxl" className="me-2" />
          <h3>All Properties</h3>
        </div>
        <div>
          <CButton
            color="info"
            className="text-white me-2"
            disabled={isLoading}
            onClick={fetchSync}
          >
            Sync Property
          </CButton>
          <Link to="/admin/add-property">
            <CButton color="info" className="text-white">
              Add New Property
            </CButton>
          </Link>
        </div>
      </div>

      {/* Property Statistics */}
      {/* Property Statistics – Dynamic version */}
      <div className="bg-white p-3 rounded my-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Property Statistics</h5>
          {statsLoading && <CSpinner color="info" size="sm" />}
        </div>

        {statsLoading ? (
          <div className="text-center py-4">
            <CSpinner color="info" />
            <p className="mt-2 text-muted">Loading statistics...</p>
          </div>
        ) : !stats ? (
          <p className="text-center text-muted py-4">No statistics available</p>
        ) : (
          <>
            {/* Total Properties – always first */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-sm-6">
                <div className="border rounded p-3 text-center bg-light">
                  <h4 className="text-primary fw-bold">
                    {stats.total?.toLocaleString() || 0}
                  </h4>
                  <p className="mb-0 text-muted small">Total Properties</p>
                </div>
              </div>

              {/* Dynamic Status Cards */}
              {Object.entries(stats.byStatus || {}).map(
                ([statusKey, count]) => {
                  // Optional: make status look nicer
                  const displayStatus = statusKey
                    .replace(/([A-Z])/g, " $1") // camelCase → space before capital
                    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
                    .trim();

                  // You can optionally map colors based on key
                  let colorClass = "info";
                  if (statusKey.toLowerCase().includes("sale"))
                    colorClass = "success";
                  if (statusKey.toLowerCase().includes("rent"))
                    colorClass = "primary";
                  if (statusKey.toLowerCase().includes("new"))
                    colorClass = "warning";
                  if (
                    statusKey.toLowerCase().includes("sold") ||
                    statusKey.toLowerCase().includes("off")
                  )
                    colorClass = "secondary";

                  return (
                    <div key={statusKey} className="col-md-3 col-sm-6">
                      <div className="border rounded p-3 text-center">
                        <h4 className={`text-${colorClass} fw-bold`}>
                          {count.toLocaleString()}
                        </h4>
                        <p className="mb-0 text-muted small">{displayStatus}</p>
                      </div>
                    </div>
                  );
                },
              )}
            </div>

            {/* Property Types – second row */}
            {Object.keys(stats.byType || {}).length > 0 && (
              <>
                <h6 className="mb-3 mt-4">By Property Type</h6>
                <div className="row g-3">
                  {Object.entries(stats.byType).map(([type, count]) => (
                    <div key={type} className="col-md-3 col-sm-6 col-6">
                      <div className="border rounded p-3 text-center">
                        <h5 className="fw-bold">{count.toLocaleString()}</h5>
                        <p className="mb-0 text-muted small text-capitalize">
                          {type}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="bg-white p-3 rounded my-4">
        {loading ? (
          <div className="text-center py-5">
            <CSpinner color="info" />
            <p className="mt-3">Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-5">
            <h5>No properties found</h5>
            <p className="text-muted">Start by adding your first property</p>
          </div>
        ) : (
          <div className="table-responsive">
            <CTable striped hover>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell scope="col">#</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Image</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Title</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Price</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Type</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Status</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Bedrooms</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Location</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Size (sqft)</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Actions</CTableHeaderCell>
                  <CTableHeaderCell scope="col">View</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {properties.map((property, index) => {
                  const urlTitle = (property.urlTitle || property.title || "")
                    .trim()
                    .toLowerCase()
                    .replace(/\s+/g, "-");
                  return (
                    <CTableRow key={property._id}>
                      <CTableHeaderCell scope="row">
                        {index + 1}
                      </CTableHeaderCell>
                      <CTableDataCell>
                        <img
                          src={getPrimaryImage(property.images)}
                          alt={property.title}
                          style={{
                            width: "80px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            e.target.src = errorImage;
                          }}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <strong>{property.title}</strong>
                        <br />
                        <small className="text-muted">
                          {property.urlTitle}
                        </small>
                      </CTableDataCell>
                      <CTableDataCell>
                        <strong>
                          {formatPrice(property.price, property.priceCurrency)}
                        </strong>
                      </CTableDataCell>
                      <CTableDataCell>{property.propertyType}</CTableDataCell>
                      <CTableDataCell>
                        {getStatusBadge(property.status)}
                      </CTableDataCell>
                      <CTableDataCell>
                        {property.bedrooms ? `${property.bedrooms} BR` : "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {property.location?.community || "N/A"}
                        {property.location?.subCommunity && (
                          <>
                            <br />
                            <small className="text-muted">
                              {property.location.subCommunity}
                            </small>
                          </>
                        )}
                      </CTableDataCell>
                      <CTableDataCell>
                        {property.propertySizeSqft
                          ? property.propertySizeSqft.toLocaleString()
                          : "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex gap-2">
                          {/* <Link to={`/admin/property/edit/${property._id}`}> */}
                          <Link to={`/admin/edit-property/${urlTitle}`}>
                            <CButton
                              color="warning"
                              size="sm"
                              className="text-white"
                            >
                              <CIcon icon={cilPencil} />
                            </CButton>
                          </Link>
                          <CButton
                            color="danger"
                            size="sm"
                            onClick={() => handleDelete(property._id)}
                            disabled={deleteLoading === property._id}
                          >
                            {deleteLoading === property._id ? (
                              <CSpinner size="sm" />
                            ) : (
                              <CIcon icon={cilTrash} />
                            )}
                          </CButton>
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CButton
                          color="info"
                          size="sm"
                          className="text-white"
                          onClick={() => handleView(property._id)}
                        >
                          {/* <CIcon icon={cilEye} /> */}
                          View
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );
                })}
              </CTableBody>
            </CTable>
            {!loading && properties.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  Showing {properties.length} properties
                  {/* {totalProperties ? ` of ${totalProperties}` : ""} */}
                </div>

                <div>
                  {hasMore && (
                    <CButton
                      color="info"
                      variant="outline"
                      size="sm"
                      disabled={loading}
                      onClick={() => fetchProperties(true)} // ← Load More
                    >
                      {loading ? <CSpinner size="sm" /> : "Load More"}
                    </CButton>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Property Details Modal */}
      <CModal visible={!!selectedProperty} onClose={closeViewModal} size="lg">
        <CModalHeader>
          <CModalTitle>
            Property Details - {selectedProperty?.title}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedProperty && (
            <div>
              <CRow>
                <CCol md="6">
                  <h6>Basic Information</h6>
                  <p>
                    <strong>Title:</strong> {selectedProperty.title}
                  </p>
                  <p>
                    <strong>URL Title:</strong> {selectedProperty.urlTitle}
                  </p>
                  <p>
                    <strong>Property Type:</strong>{" "}
                    {selectedProperty.propertyType}
                  </p>
                  <p>
                    <strong>Category:</strong> {selectedProperty.category}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {getStatusBadge(selectedProperty.status)}
                  </p>
                  <p>
                    <strong>Price:</strong>{" "}
                    {formatPrice(
                      selectedProperty.price,
                      selectedProperty.priceCurrency,
                    )}
                  </p>
                  <p>
                    <strong>Description:</strong> {selectedProperty.description}
                  </p>
                  <p>
                    <strong>Bedrooms:</strong> {selectedProperty.bedrooms}
                  </p>
                  <p>
                    <strong>Bathrooms:</strong> {selectedProperty.bathrooms}
                  </p>
                  <p>
                    <strong>Property Size:</strong>{" "}
                    {selectedProperty.propertySizeSqft} sqft
                  </p>
                  <p>
                    <strong>Build Year:</strong> {selectedProperty.buildYear}
                  </p>
                  <p>
                    <strong>Permit Number:</strong>{" "}
                    {selectedProperty.permitNumber}
                  </p>
                  <p>
                    <strong>Views:</strong> {selectedProperty.views}
                  </p>
                </CCol>
                <CCol md="6">
                  {selectedProperty.images &&
                    selectedProperty.images.length > 0 && (
                      <div className="mb-3">
                        <h6>Images:</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedProperty.images.map((img, index) => (
                            <img
                              key={index}
                              src={img.url}
                              alt={`Property Image ${index + 1}`}
                              style={{
                                width: "100px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                  {selectedProperty.location && (
                    <div className="mb-3">
                      <h6>Location:</h6>
                      <p>
                        <strong>Address:</strong>{" "}
                        {selectedProperty.location.address}
                      </p>
                      <p>
                        <strong>Community:</strong>{" "}
                        {selectedProperty.location.community}
                      </p>
                      <p>
                        <strong>Sub-Community:</strong>{" "}
                        {selectedProperty.location.subCommunity}
                      </p>
                      <p>
                        <strong>City:</strong> {selectedProperty.location.city}
                      </p>
                      <p>
                        <strong>Zip Code:</strong>{" "}
                        {selectedProperty.location.zipCode}
                      </p>
                      <p>
                        <strong>Latitude:</strong>{" "}
                        {selectedProperty.location.latitude}
                      </p>
                      <p>
                        <strong>Longitude:</strong>{" "}
                        {selectedProperty.location.longitude}
                      </p>
                    </div>
                  )}

                  {selectedProperty.amenities &&
                    selectedProperty.amenities.length > 0 && (
                      <div className="mb-3">
                        <h6>Amenities:</h6>
                        <ul>
                          {selectedProperty.amenities.map((amenity, index) => (
                            <li key={index}>{amenity}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                </CCol>
              </CRow>

              <CRow className="mt-3">
                <CCol md="6">
                  {selectedProperty.priceInsights && (
                    <div className="mb-3">
                      <h6>Price Insights:</h6>
                      <p>
                        <strong>Average Price per Sqft:</strong>{" "}
                        {selectedProperty.priceInsights.averagePricePerSqft}
                      </p>
                      <p>
                        <strong>Comparison to Area:</strong>{" "}
                        {selectedProperty.priceInsights.comparisonToArea}
                      </p>
                      <p>
                        <strong>Similar Property Comparison:</strong>{" "}
                        {
                          selectedProperty.priceInsights
                            .similarPropertyComparison
                        }
                      </p>
                    </div>
                  )}

                  {selectedProperty.agent && (
                    <div className="mb-3">
                      <h6>Agent:</h6>
                      <p>
                        <strong>Name:</strong>{" "}
                        {selectedProperty.agent.name || "—"}
                      </p>
                      {selectedProperty.agent.agentLicense && (
                        <p>
                          <strong>License:</strong>{" "}
                          {selectedProperty.agent.agentLicense}
                        </p>
                      )}
                    </div>
                  )}

                  {selectedProperty.developer && (
                    <div className="mb-3">
                      <h6>Developer:</h6>
                      <p>
                        <strong>Name:</strong>{" "}
                        {selectedProperty.developer.name || "—"}
                      </p>
                      {selectedProperty.developer.logo && (
                        <img
                          src={selectedProperty.developer.logo}
                          alt={selectedProperty.developer.name}
                          style={{ maxWidth: "120px", marginTop: "8px" }}
                        />
                      )}
                    </div>
                  )}

                  {selectedProperty.mortgageInformation && (
                    <div className="mb-3">
                      <h6>Mortgage Information:</h6>
                      <p>
                        <strong>Estimated Monthly Payment (AED):</strong>{" "}
                        {
                          selectedProperty.mortgageInformation
                            .estimatedMonthlyPaymentAED
                        }
                      </p>
                      <p>
                        <strong>Interest Rate:</strong>{" "}
                        {selectedProperty.mortgageInformation.interestRate}%
                      </p>
                      <p>
                        <strong>Down Payment (AED):</strong>{" "}
                        {selectedProperty.mortgageInformation.downPaymentAED}
                      </p>
                      <p>
                        <strong>Loan Amount (AED):</strong>{" "}
                        {selectedProperty.mortgageInformation.loanAmountAED}
                      </p>
                      <p>
                        <strong>Loan Period (Years):</strong>{" "}
                        {selectedProperty.mortgageInformation.loanPeriodYears}
                      </p>
                    </div>
                  )}
                </CCol>
                <CCol md="6">
                  <div className="mb-3">
                    <h6>Dates:</h6>
                    <p>
                      <strong>Listed Date:</strong>{" "}
                      {new Date(
                        selectedProperty.listedDate,
                      ).toLocaleDateString()}
                    </p>
                    <p>
                      <strong>Updated Date:</strong>{" "}
                      {new Date(
                        selectedProperty.updatedDate,
                      ).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="mb-3">
                    <h6>Other Details:</h6>
                    <p>
                      <strong>Payment Method:</strong>{" "}
                      {selectedProperty.paymentMethod}
                    </p>
                    <p>
                      <strong>Furnishing:</strong> {selectedProperty.furnishing}
                    </p>
                    <p>
                      <strong>Completion Status:</strong>{" "}
                      {selectedProperty.completionStatus}
                    </p>
                    <p>
                      <strong>Listing Type:</strong>{" "}
                      {selectedProperty.listingType}
                    </p>
                    <p>
                      <strong>Purpose:</strong> {selectedProperty.purpose}
                    </p>
                  </div>

                  {selectedProperty.regulatoryInformation && (
                    <div className="mb-3">
                      <h6>Regulatory Information:</h6>
                      <p>
                        <strong>Reference:</strong>{" "}
                        {selectedProperty.regulatoryInformation.reference}
                      </p>
                      <p>
                        <strong>Broker License:</strong>{" "}
                        {selectedProperty.regulatoryInformation.brokerLicense}
                      </p>
                      <p>
                        <strong>Agency Name:</strong>{" "}
                        {selectedProperty.regulatoryInformation.agencyName}
                      </p>
                      <p>
                        <strong>Zone Name:</strong>{" "}
                        {selectedProperty.regulatoryInformation.zoneName}
                      </p>
                      <p>
                        <strong>DLD Permit Number:</strong>{" "}
                        {selectedProperty.regulatoryInformation.dldPermitNumber}
                      </p>
                      <p>
                        <strong>Agent License:</strong>{" "}
                        {selectedProperty.regulatoryInformation.agentLicense}
                      </p>
                    </div>
                  )}
                  <p>
                    <strong>Is Deleted:</strong>{" "}
                    {selectedProperty.isDeleted ? "Yes" : "No"}
                  </p>
                </CCol>
              </CRow>
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={closeViewModal}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default PropertyList;
